import React, { useState } from "react";
import { login, seleccionarEmpresa, EmpresaDto, GrupoEmpresaDto } from "../services/api";
import logo from "../kyb.ico";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [todasEmpresas, setTodasEmpresas] = useState<EmpresaDto[]>([]);
  const [grupos, setGrupos] = useState<GrupoEmpresaDto[]>([]);
  const [grupoElegido, setGrupoElegido] = useState<number | null>(null);
  const [empresaElegida, setEmpresaElegida] = useState<number | null>(null);
  const [usuarioId, setUsuarioId] = useState<number | null>(null);
  const [token, setToken] = useState("");
  const [nombre, setNombre] = useState("");
  const [emailUsuario, setEmailUsuario] = useState("");
  const [esSuperUsuario, setEsSuperUsuario] = useState(false);
  const [paso, setPaso] = useState<"login" | "seleccion">("login");

  const ordenarEmpresas = (lista: EmpresaDto[]) =>
    [...lista].sort((a, b) => {
      if (!a.ultimoAcceso) return 1;
      if (!b.ultimoAcceso) return -1;
      return new Date(b.ultimoAcceso).getTime() - new Date(a.ultimoAcceso).getTime();
    });

  const empresasParaGrupo = (gId: number | null, todas: EmpresaDto[]) =>
    ordenarEmpresas(gId !== null ? todas.filter(e => e.grupoEmpresaId === gId) : todas);

  const entrarConEmpresa = async (
    tk: string, uid: number, nom: string, eml: string,
    empresa: EmpresaDto, todas: EmpresaDto[], grupoId: number | null,
    gruposList: GrupoEmpresaDto[], esSuper: boolean
  ) => {
    await seleccionarEmpresa(uid, empresa.empresaId, tk);
    localStorage.setItem("token", tk);
    localStorage.setItem("usuario", JSON.stringify({ nombre: nom, email: eml, usuarioId: uid, esSuperUsuario: esSuper }));
    localStorage.setItem("empresa", JSON.stringify({ empresaId: empresa.empresaId, nombre: empresa.nombre }));
    localStorage.setItem("empresas", JSON.stringify(todas));
    localStorage.setItem("grupos", JSON.stringify(gruposList));
    localStorage.setItem("grupoActivo", grupoId ? String(grupoId) : "");
    await new Promise(resolve => setTimeout(resolve, 100));
    window.location.href = "/dashboard";
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);

      if (data.empresas.length === 0) {
        setError("Tu usuario no tiene empresas habilitadas.");
        return;
      }

      setToken(data.token);
      setUsuarioId(data.usuarioId);
      setNombre(data.nombre);
      setEmailUsuario(data.email);
      setTodasEmpresas(data.empresas);
      setGrupos(data.grupos);
      setEsSuperUsuario(data.esSuperUsuario);

      const primerGrupo = data.grupos.length > 0 ? data.grupos[0].grupoEmpresaId : null;
      const empresasIniciales = empresasParaGrupo(primerGrupo, data.empresas);

      // Auto-entrar si no hay ambigüedad: un solo grupo (o ninguno) y una sola empresa posible
      if (data.grupos.length <= 1 && empresasIniciales.length === 1) {
        await entrarConEmpresa(
          data.token, data.usuarioId, data.nombre, data.email,
          empresasIniciales[0], data.empresas, primerGrupo, data.grupos, data.esSuperUsuario
        );
        return;
      }

      setGrupoElegido(primerGrupo);
      setEmpresaElegida(empresasIniciales[0]?.empresaId ?? null);
      setPaso("seleccion");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarGrupoEnLogin = (gId: number) => {
    setGrupoElegido(gId);
    const filtradas = empresasParaGrupo(gId, todasEmpresas);
    setEmpresaElegida(filtradas[0]?.empresaId ?? null);
  };

  const handleEntrar = async () => {
    const filtradas = empresasParaGrupo(grupoElegido, todasEmpresas);
    const emp = filtradas.find(e => e.empresaId === empresaElegida);
    if (!emp || !usuarioId) return;
    setLoading(true);
    setError("");
    try {
      await entrarConEmpresa(token, usuarioId, nombre, emailUsuario, emp, todasEmpresas, grupoElegido, grupos, esSuperUsuario);
    } catch {
      setError("Error al ingresar.");
    } finally {
      setLoading(false);
    }
  };

  const empresasMostradas = empresasParaGrupo(grupoElegido, todasEmpresas);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
 <div style={styles.headerRow}>
          <img src={logo} alt="KYBiz" style={styles.logo} />
          <div style={styles.brandName}>
            <span style={styles.brandKYB}>KYB</span><span style={styles.brandIz}>iz</span>
          </div>
        </div>
        <div style={styles.brandSub}>KNOW YOUR BUSINESS</div>       
        {paso === "login" && (
          <>
            <p style={styles.subtitle}>Iniciá sesión para continuar</p>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={handleLogin}>
              <div style={styles.field}>
                <label style={styles.label}>Email</label>
                <input style={styles.input} type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Contraseña</label>
                <input style={styles.input} type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
              </div>
              <button style={styles.button} type="submit" disabled={loading}>
                {loading ? "Ingresando..." : "Ingresar"}
              </button>
            </form>
          </>
        )}

        {paso === "seleccion" && (
          <>
            <p style={styles.subtitle}>Seleccioná dónde ingresar</p>
            {error && <div style={styles.error}>{error}</div>}

            {grupos.length > 1 && (
              <div style={styles.field}>
                <label style={styles.label}>Grupo</label>
                <select style={styles.input} value={grupoElegido ?? ""}
                  onChange={(e) => handleCambiarGrupoEnLogin(Number(e.target.value))}>
                  {grupos.map(g => (
                    <option key={g.grupoEmpresaId} value={g.grupoEmpresaId}>{g.nombre}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>Empresa</label>
              {empresasMostradas.length === 0 ? (
                <div style={styles.error}>Este grupo no tiene empresas habilitadas.</div>
              ) : (
                <select style={styles.input} value={empresaElegida ?? ""}
                  onChange={(e) => setEmpresaElegida(Number(e.target.value))}>
                  {empresasMostradas.map(emp => (
                    <option key={emp.empresaId} value={emp.empresaId}>
                      {emp.nombre}
                      {emp.ultimoAcceso ? ` (${new Date(emp.ultimoAcceso).toLocaleString("es-UY")})` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <button style={styles.button} onClick={handleEntrar}
              disabled={loading || empresasMostradas.length === 0}>
              {loading ? "Entrando..." : "Entrar"}
            </button>
            <button style={styles.buttonSecondary} onClick={() => setPaso("login")}>Volver</button>
          </>
        )}
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f2f5" },
  card: { backgroundColor: "#fff", padding: "40px", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", width: "100%", maxWidth: "380px", textAlign: "center" },
  headerRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "8px" },
  logo: { width: "70px", height: "70px" },
  brandName: { display: "inline-flex", alignItems: "baseline" },
  brandKYB: { fontFamily: "'Bebas Neue', sans-serif", fontSize: "48px", color: "#111", lineHeight: 1 },
  brandIz: { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: "26px", color: "#111", lineHeight: 1, marginLeft: "1px" },
  brandSub: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: "11px", fontWeight: 300, letterSpacing: "3px", color: "#888", marginBottom: "24px" },
  subtitle: { margin: "0 0 28px 0", color: "#666", fontSize: "14px" },
  field: { marginBottom: "16px", textAlign: "left" },
  label: { display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: 600, color: "#333" },
  input: { width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "14px", boxSizing: "border-box", outline: "none" },
  button: { width: "100%", padding: "12px", backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: 600, cursor: "pointer", marginTop: "8px" },
  buttonSecondary: { width: "100%", padding: "10px", backgroundColor: "transparent", color: "#666", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", cursor: "pointer", marginTop: "8px" },
  error: { backgroundColor: "#fff0f0", color: "#c0392b", padding: "10px 12px", borderRadius: "8px", marginBottom: "16px", fontSize: "13px" },
};